// path: src/services/AccessRuleService.ts
import { AccessRule, User } from '../@types';
import api from '../lib/api';

export const ACCESS_RULE_TIMEOUT_MS = 5000;

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;

// A chave do gateway muda sem trocar o token: a resposta vive so na memoria da pagina e vence em minutos.
export const ACCESS_RULE_MAX_AGE_MS = 5 * MINUTE_MS;

export const LEGACY_ACCESS_RULE_STORAGE_KEY = 'autenticacao.accessRule';

const PRODUCT_RULE: AccessRule = 'PRODUCT';
const PACKAGE_RULE: AccessRule = 'PACKAGE';

type AccessRuleMemo = { token: string; askedAt: number; promise: Promise<AccessRule> };

let memo: AccessRuleMemo | null = null;

const isAccessRule = (value: unknown): value is AccessRule => value === PRODUCT_RULE || value === PACKAGE_RULE;

const isFreshFor = (current: AccessRuleMemo | null, token: string, now: number): current is AccessRuleMemo =>
  current !== null && current.token === token && now - current.askedAt < ACCESS_RULE_MAX_AGE_MS;

const clearLegacyStoredRule = (): void => {
  try {
    localStorage.removeItem(LEGACY_ACCESS_RULE_STORAGE_KEY);
  } catch {
    // Nada a limpar se o armazenamento nao responde.
  }
};

const readRule = (body: unknown): AccessRule | null => {
  if (typeof body !== 'object' || body === null) return null;
  const { rule } = body as { rule?: unknown };
  return isAccessRule(rule) ? rule : null;
};

const askGateway = (token: string): Promise<AccessRule> =>
  api({ url: '/access/rule', method: 'GET', token, timeout: ACCESS_RULE_TIMEOUT_MS })
    .then(response => readRule(response?.data) ?? PACKAGE_RULE)
    .catch(() => PACKAGE_RULE);

// Falha cai na regra de pacote sem mensagem: o cliente de src/lib/api.ts nao mostra alerta global em erro.
export const fetchAccessRule = (token: string | null | undefined): Promise<AccessRule> => {
  if (!token) return Promise.resolve(PACKAGE_RULE);
  const now = Date.now();
  if (isFreshFor(memo, token, now)) return memo.promise;

  const promise = askGateway(token);
  memo = { token, askedAt: now, promise };
  return promise;
};

export const resetAccessRule = (): void => {
  memo = null;
  clearLegacyStoredRule();
};

// Usuario e regra juntos, para a guarda nunca decidir sem a regra e depois mudar de ideia.
export const loadUserWithAccessRule = async (loadUser: () => Promise<User | null>, token: string | null | undefined): Promise<User | null> => {
  const [user, accessRule] = await Promise.all([loadUser().catch(() => null), fetchAccessRule(token)]);
  if (!user) return null;
  return { ...user, accessRule };
};
